import _ from 'lodash';
import React, { useState } from 'react';

type GhgFormProps<S> = {
  state: S[];
  setState: Function;
  id: number;
  active: undefined | number;
  setActive: Function;
};

const withWrapper = <
  S,
  CombinedS extends {},
  P extends GhgFormProps<S> & { commitState: (state: S[]) => void },
>(
  Component: React.ComponentType<P>,
  basicCombiner: (combinedData: CombinedS, state: S[]) => CombinedS,
) => {
  return ({
    handleSend,
    combinedData,
    combiner = basicCombiner,
    ...props
  }: Omit<P, 'commitState'> & {
    handleSend: (combinedData: CombinedS) => void;
    combinedData: CombinedS;
    combiner?: (combinedData: CombinedS, state: S[]) => CombinedS;
  }) => {
    //const props = { ...partialProps, ...rawProps } as any as P;
    const createState = () => {
      const newState = [...props.state];
      newState[props.id] = _.cloneDeep(props.state[props.id]);
      return newState;
    };
    const [localState, setLocalState] = useState(createState());

    React.useEffect(() => {
      setLocalState(createState());
    }, [props.state, props.id]);

    const resolvedState = props.active === props.id ? localState : props.state;

    const handleSetState = (newState: S[]) => {
      // push state only if the array has changed
      const checkRefsChanged = () => {
        for (let i = 0; i < newState.length; i++) {
          if (i === props.id) continue;
          if (newState[i] !== props.state[i]) {
            return true;
          }
          return false;
        }
      };

      const wasOutsideStateChanged =
        newState.length !== props.state.length || checkRefsChanged();
      if (wasOutsideStateChanged) {
        props.setState(newState);
      } else {
        setLocalState(newState);
      }
    };

    const commitState = (state: S[]) => {
      const data = combiner(combinedData, state);
      props.setState(state);
      props.setActive(undefined);
      handleSend(data);
    };

    return (
      <Component
        {...(props as any as P)}
        state={resolvedState}
        setState={handleSetState}
        commitState={commitState}
      />
    );
  };
};

const withAddWrapper = <S, P extends GhgFormProps<S>>(
  Component: React.ComponentType<P>
) => {
  return (props: Omit<P, 'combinedData' | 'setState' | 'id'>) => (
    <Component
      {...(props as P)}
      combinedData={{}}
      active={props.active === -1 ? 0 : props.active}
      setActive={(x: any) => props.setActive(x === undefined ? undefined : -1)}
      setState={() => {}}
      id={0}
    />
  );
};

export { withWrapper, withAddWrapper };
